import { useEffect } from 'react';
import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { create } from 'zustand';
import { SupabaseRemote, SyncEngine, type SyncStatus } from '@nexus/sync';
import { useStore } from '../db/context';
import { server, serverConfigured } from './config';

// The schema name is configurable per self-hosted project, not the literal "public" type.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Client = SupabaseClient<any, string>;

let client: Client | null = null;

export function supabase(): Client {
  client ??= createClient(server().url, server().key, {
    auth: { persistSession: true, autoRefreshToken: true, storageKey: 'nexus-auth' },
    db: { schema: server().schema },
  });
  return client;
}

interface SyncUi {
  /** The stored login has been read (session is known, signed in or not). */
  authReady: boolean;
  session: Session | null;
  status: SyncStatus;
  engine: SyncEngine | null;
}

export const useSyncState = create<SyncUi>(() => ({
  authReady: false,
  session: null,
  status: { state: 'signed-out', lastSyncAt: null, pending: 0, error: null },
  engine: null,
}));

const LOCAL_CHANGE_DELAY_MS = 1_500;
const REMOTE_CHANGE_DELAY_MS = 400;
const INTERVAL_MS = 60_000;

/** Keeps this device in sync while signed in: on changes, realtime pings, periodically. */
export function useSync() {
  const store = useStore();

  useEffect(() => {
    // No server set up: this device only, nothing goes over the network.
    if (!serverConfigured()) {
      useSyncState.setState({ authReady: true });
      return;
    }
    const sb = supabase();
    const engine = new SyncEngine(store, new SupabaseRemote(sb));
    useSyncState.setState({ engine });
    const offStatus = engine.onStatus((status) => useSyncState.setState({ status }));

    let timer: ReturnType<typeof setTimeout> | undefined;
    const soon = (ms: number) => {
      clearTimeout(timer);
      timer = setTimeout(() => void engine.sync().catch(() => {}), ms);
    };

    // Local edits → push shortly after.
    const offLocal = store.subscribe(() => {
      if (useSyncState.getState().session) soon(LOCAL_CHANGE_DELAY_MS);
    }, ['pages', 'props', 'content', 'attachments', 'settings']);

    // Changes from other devices → realtime ping → pull.
    let channel: ReturnType<SupabaseClient['channel']> | null = null;
    const listen = (session: Session | null) => {
      if (channel) void sb.removeChannel(channel);
      channel = null;
      if (!session) return;
      const filter = `owner_id=eq.${session.user.id}`;
      const schema = server().schema;
      channel = sb
        .channel('nexus-sync')
        .on('postgres_changes', { event: '*', schema, table: 'records', filter }, () =>
          soon(REMOTE_CHANGE_DELAY_MS),
        )
        .on('postgres_changes', { event: 'INSERT', schema, table: 'page_updates', filter }, () =>
          soon(REMOTE_CHANGE_DELAY_MS),
        )
        .subscribe();
    };

    const { data: auth } = sb.auth.onAuthStateChange((_event, session) => {
      useSyncState.setState({ session, authReady: true });
      listen(session);
      if (session) soon(0);
      else useSyncState.setState({ status: { ...engine.status, state: 'signed-out' } });
    });

    const interval = setInterval(() => useSyncState.getState().session && soon(0), INTERVAL_MS);
    const onOnline = () => soon(0);
    const onVisible = () =>
      document.visibilityState === 'visible' && useSyncState.getState().session && soon(0);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      offStatus();
      offLocal();
      auth.subscription.unsubscribe();
      if (channel) void sb.removeChannel(channel);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [store]);
}

export async function signIn(email: string, password: string) {
  const { error } = await supabase().auth.signInWithPassword({ email, password });
  if (error) throw error;
}

export async function signUp(email: string, password: string) {
  const { data, error } = await supabase().auth.signUp({ email, password });
  if (error) throw error;
  return { needsConfirmation: !data.session };
}

/** Push what is left, then sign out. Local data stays on this device. */
export async function signOut() {
  await useSyncState
    .getState()
    .engine?.sync()
    .catch(() => {});
  await supabase().auth.signOut();
}
